import { SuperOpsStateManager } from "./SuperOpsStateManager";
import { cache, ZomentumStorageKeys } from "@/Contracts";
import { isEmbedded } from "@/Utils/superops/embedDetection";
import { SuperOpsState } from "./interfaces";
import { SuperOpsSdkService } from "./SuperOpsSdkService";
import { buildSuperOpsAuthorizeTarget } from "@/superops/utils/buildSuperOpsAuthorizeTarget";
import { prependBasePath } from "@/Utils/routing/getAppPathname";

/**
 * Bridges the SuperOps host SDK state lifecycle to Zomentum's Redux/local state.
 * The host drives this via SDK events: `restoreData` (pushed after the widget
 * mounts) and `persistData` (pulled before it unmounts). The sessionStorage
 * pending-restore path is retained for our own internal login redirect, which
 * the host's persist/restore cannot survive.
 */
class SuperOpsMessageService {
	private static instance: SuperOpsMessageService;
	private stateManager: SuperOpsStateManager;
	private isInitialized = false;

	private constructor() {
		this.stateManager = new SuperOpsStateManager();
	}

	static getInstance(): SuperOpsMessageService {
		if (!SuperOpsMessageService.instance) {
			SuperOpsMessageService.instance = new SuperOpsMessageService();
		}
		return SuperOpsMessageService.instance;
	}

	initialize(): void {
		if (this.isInitialized) return;

		// Only initialize if we're in embedded context
		if (!isEmbedded()) return;

		this.registerSdkHandlers();
		this.checkForPendingRestoration();
		this.isInitialized = true;
	}

	private registerSdkHandlers(): void {
		SuperOpsSdkService.onRestoreData(saved => {
			if (saved) {
				void this.handleRestoreState(saved);
			}
		});
		SuperOpsSdkService.onPersistData(() => this.gatherCurrentData());
	}

	private async handleRestoreState(
		restoreState: SuperOpsState
	): Promise<void> {
		try {
			if (!this.isAuthenticated()) {
				this.stateManager.saveRestoreStateForLater(restoreState);
				await this.navigateToSsoEntry();
				return;
			}

			await this.stateManager.restoreState(restoreState);
		} catch (error) {
			console.error("Failed to handle restore state:", error);
		}
	}

	private gatherCurrentData(): SuperOpsState {
		try {
			if (!this.isAuthenticated()) {
				console.warn(
					"SuperOps persist data skipped: User not authenticated"
				);
				return { route: "" };
			}
			return this.stateManager.getCurrentState();
		} catch (error) {
			console.error("Failed to gather current data:", error);
			return { route: "" };
		}
	}

	private isAuthenticated(): boolean {
		try {
			return !!cache.get(ZomentumStorageKeys.Token);
		} catch (error) {
			return false;
		}
	}

	private async navigateToSsoEntry(): Promise<void> {
		const target = prependBasePath(buildSuperOpsAuthorizeTarget());
		window.history.pushState(null, "", target);
		const event = new PopStateEvent("popstate");
		window.dispatchEvent(event);
	}

	hasPendingRestoreState(): boolean {
		return (
			isEmbedded() &&
			this.isAuthenticated() &&
			this.stateManager.checkForPendingRestorationSilent()
		);
	}

	restorePendingState(): boolean {
		if (!isEmbedded()) return false;
		if (!this.isAuthenticated()) return false;
		this.stateManager.checkForPendingRestoration();
		return true;
	}

	private checkForPendingRestoration(): void {
		if (!this.isAuthenticated()) return;
		this.stateManager.checkForPendingRestoration();
	}
}

export { SuperOpsMessageService };
